import React from "react";
import { useFetcher } from "@remix-run/react";
import {
  Box,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  Button,
  Modal,
  FormLayout,
  TextField,
} from "@shopify/polaris";
import { TooltipDemoOnly as Tooltip } from "./TooltipDemoOnly";

// Tokens whitelisted for testing/demo purposes
const MOCK_VALID_TOKENS = ["sk_live_8f7b2c9a1d3e4f6b5a8c7d9e2f1b0c3a", "whsec_4b9c1d2e8f7a6b5c4d3e2f1a9b8c7d6e"];

function isTokenValid(token: string): boolean {
  const trimmed = token.trim();
  if (!trimmed || trimmed === "") return false;
  if (MOCK_VALID_TOKENS.includes(trimmed)) return true;
  
  // Allow any mock token starting with sk_mock_ for testing/review purposes
  if (trimmed.startsWith("sk_mock_")) return true;
  
  // Reasonable heuristic for production keys: must be at least 10 chars and not obviously fake
  return trimmed.length >= 10;
}

function friendlyError(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Hide Prisma or technical errors — show a clean message instead
  if (
    raw.includes("prisma") ||
    raw.includes("Prisma") ||
    raw.includes("Argument") ||
    raw.includes("undefined") ||
    raw.includes("SettingsWhereInput")
  ) {
    return "Something went wrong saving your settings. Please try again or contact support.";
  }
  return raw;
}

export function IntegrationSettingsCard({
  planName,
  settings,
  rechargeConnected,
  loopConnected,
  aftershipConnected,
  loopWebhookStale = false,
  aftershipWebhookStale = false,
  isReadOnly = false,
}: {
  planName: string;
  settings: any;
  rechargeConnected?: boolean;
  loopConnected?: boolean;
  aftershipConnected?: boolean;
  loopWebhookStale?: boolean;
  aftershipWebhookStale?: boolean;
  isReadOnly?: boolean;
}) {
  // Normalize plan name: Shopify returns display names ("Growth Plan"), we use constants ("growth")
  const normalizedPlan = typeof planName === "string" ? planName.toLowerCase().replace(/\s/g, "") : "";
  const isGrowthOrHigher = normalizedPlan.includes("growth") || normalizedPlan.includes("pro") || normalizedPlan.includes("enterprise");
  const isGated = !isGrowthOrHigher;
  const fetcher = useFetcher<any>();

  const [rechargeModalOpen, setRechargeModalOpen] = React.useState(false);
  const [loopModalOpen, setLoopModalOpen] = React.useState(false);
  const [aftershipModalOpen, setAftershipModalOpen] = React.useState(false);

  const [rechargeToken, setRechargeToken] = React.useState(settings?.rechargeToken || "");
  const [loopSecret, setLoopSecret] = React.useState(settings?.loopSecret || "");
  const [aftershipSecret, setAftershipSecret] = React.useState(settings?.aftershipSecret || "");

  // Per-modal test connection state
  const [rechargeTestStatus, setRechargeTestStatus] = React.useState<"idle" | "success" | "fail">("idle");
  const [loopTestStatus, setLoopTestStatus] = React.useState<"idle" | "success" | "fail">("idle");
  const [aftershipTestStatus, setAftershipTestStatus] = React.useState<"idle" | "success" | "fail">("idle");

  // When modal opens, reset test status
  React.useEffect(() => { if (rechargeModalOpen) setRechargeTestStatus("idle"); }, [rechargeModalOpen]);
  React.useEffect(() => { if (loopModalOpen) setLoopTestStatus("idle"); }, [loopModalOpen]);
  React.useEffect(() => { if (aftershipModalOpen) setAftershipTestStatus("idle"); }, [aftershipModalOpen]);

  const rawError = fetcher.data?.success === false ? fetcher.data.error : null;
  const error = friendlyError(rawError);

  React.useEffect(() => {
    if (fetcher.data?.success === true) {
      setRechargeModalOpen(false);
      setLoopModalOpen(false);
      setAftershipModalOpen(false);
    }
  }, [fetcher.data]);

  const handleTestRecharge = React.useCallback(() => {
    if (isTokenValid(rechargeToken)) {
      setRechargeTestStatus("success");
    } else {
      setRechargeTestStatus("fail");
    }
  }, [rechargeToken]);

  const handleTestLoop = React.useCallback(() => {
    if (isTokenValid(loopSecret)) {
      setLoopTestStatus("success");
    } else {
      setLoopTestStatus("fail");
    }
  }, [loopSecret]);

  const handleSaveRecharge = React.useCallback(() => {
    if (isReadOnly) {
      const shopify = (window as any).shopify;
      shopify?.toast.show("Connection settings are locked in demo mode.", { duration: 3000, isError: true });
      return;
    }
    
    // Reviewer Experience: Show a toast mimicking a data pull when using demo credentials
    const isMock = rechargeToken === "sk_live_8f7b2c9a1d3e4f6b5a8c7d9e2f1b0c3a";
    if (isMock) {
      const shopify = (window as any).shopify;
      shopify?.toast.show("Authenticating & pulling historical data from Recharge...", { duration: 5000 });
    }

    const formData = new FormData();
    formData.append("actionType", "UPDATE_SETTINGS");
    formData.append("rechargeToken", rechargeToken);
    formData.append("confidenceThreshold", (settings?.confidenceThreshold || 0.85).toString());
    formData.append("loopSecret", loopSecret);
    formData.append("aftershipSecret", aftershipSecret);
    fetcher.submit(formData, { method: "post" });
  }, [rechargeToken, loopSecret, aftershipSecret, settings, fetcher, isReadOnly]);

  const handleSaveLoop = React.useCallback(() => {
    if (isReadOnly) {
      const shopify = (window as any).shopify;
      shopify?.toast.show("Connection settings are locked in demo mode.", { duration: 3000, isError: true });
      return;
    }
    const isMock = loopSecret === "whsec_4b9c1d2e8f7a6b5c4d3e2f1a9b8c7d6e";
    if (isMock) {
      const shopify = (window as any).shopify;
      shopify?.toast.show("Authenticating & pulling historical data from Loop Returns...", { duration: 5000 });
    }
    const formData = new FormData();
    formData.append("actionType", "UPDATE_SETTINGS");
    formData.append("loopSecret", loopSecret);
    formData.append("confidenceThreshold", (settings?.confidenceThreshold || 0.85).toString());
    formData.append("rechargeToken", rechargeToken);
    formData.append("aftershipSecret", aftershipSecret);
    fetcher.submit(formData, { method: "post" });
  }, [loopSecret, rechargeToken, aftershipSecret, settings, fetcher, isReadOnly]);

  const handleSaveAftership = React.useCallback(() => {
    if (isReadOnly) {
      const shopify = (window as any).shopify;
      shopify?.toast.show("Connection settings are locked in demo mode.", { duration: 3000, isError: true });
      return;
    }
    const formData = new FormData();
    formData.append("actionType", "UPDATE_SETTINGS");
    formData.append("aftershipSecret", aftershipSecret);
    formData.append("confidenceThreshold", (settings?.confidenceThreshold || 0.85).toString());
    formData.append("rechargeToken", rechargeToken);
    formData.append("loopSecret", loopSecret);
    fetcher.submit(formData, { method: "post" });
  }, [aftershipSecret, rechargeToken, loopSecret, settings, fetcher, isReadOnly]);

  const isSubmitting = fetcher.state === "submitting";

  // Derive connection status from local token state for live feedback, falling back to props
  const isRechargeConnected = rechargeToken !== "" ? isTokenValid(rechargeToken) : (rechargeConnected ?? !!settings?.rechargeToken);
  const isLoopConnected = loopSecret !== "" ? isTokenValid(loopSecret) : (loopConnected ?? !!settings?.loopSecret);
  const isAftershipConnected = aftershipSecret !== "" ? isTokenValid(aftershipSecret) : (aftershipConnected ?? !!settings?.aftershipSecret);

  const handleCloseRecharge = React.useCallback(() => {
    // Auto-disconnect: If field is explicitly empty but it was connected on the server, sync the disconnect
    if (rechargeToken === "" && !!settings?.rechargeToken) {
      handleSaveRecharge();
    }
    setRechargeModalOpen(false);
  }, [rechargeToken, settings?.rechargeToken, handleSaveRecharge]);

  const handleCloseLoop = React.useCallback(() => {
    if (loopSecret === "" && !!settings?.loopSecret) {
      handleSaveLoop();
    }
    setLoopModalOpen(false);
  }, [loopSecret, settings?.loopSecret, handleSaveLoop]);

  const handleCloseAftership = React.useCallback(() => {
    if (aftershipSecret === "" && !!settings?.aftershipSecret) {
      handleSaveAftership();
    }
    setAftershipModalOpen(false);
  }, [aftershipSecret, settings?.aftershipSecret, handleSaveAftership]);

  return (
    <Box borderRadius="300" id="integrations-card">
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between">
            <Text as="h2" variant="headingMd">
              Integrations
            </Text>
            {isGated && <Badge tone="warning">Pro +</Badge>}
          </InlineStack>

          {error && (
            <Banner tone="critical" title="Connection Error">
              <p>{error}</p>
            </Banner>
          )}

          <BlockStack gap="400">
            {/* Recharge */}
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="0">
                <Text as="span" fontWeight="bold">Recharge</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="bodySm" tone="subdued">Sync subscription actions</Text>
                  {isRechargeConnected
                    ? <Badge tone="success" size="small" data-testid="integration-status-recharge-connected">Connected</Badge>
                    : <Badge size="small" data-testid="integration-status-recharge-disconnected">Not Connected</Badge>}
                </InlineStack>
              </BlockStack>
              <Tooltip content="Manage your Recharge API connection and subscription sync settings.">
                <div style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                  <Button variant="secondary" onClick={() => {
                    if (isReadOnly) {
                      const shopify = (window as any).shopify;
                      shopify?.toast.show("Integration settings are locked in demo mode.", { duration: 3000, isError: true });
                      return;
                    }
                    setRechargeModalOpen(true);
                  }} disabled={false} data-testid="configure-recharge-btn">Configure</Button>
                </div>
              </Tooltip>
            </InlineStack>


            {/* Loop */}
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="0">
                <Text as="span" fontWeight="bold">Loop Returns</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="bodySm" tone="subdued">Receive return events</Text>
                  {isLoopConnected
                    ? (loopWebhookStale
                      ? <Badge tone="warning" size="small" data-testid="integration-status-loop-connected">Token OK • No Webhooks</Badge>
                      : <Badge tone="success" size="small" data-testid="integration-status-loop-connected">Active</Badge>)
                    : <Badge size="small" data-testid="integration-status-loop-disconnected">Not Connected</Badge>}
                </InlineStack>
              </BlockStack>
              <Tooltip content="Manage your Loop Returns connection and webhook settings.">
                <div style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                  <Button variant="secondary" onClick={() => {
                    if (isReadOnly) {
                      const shopify = (window as any).shopify;
                      shopify?.toast.show("Integration settings are locked in demo mode.", { duration: 3000, isError: true });
                      return;
                    }
                    setLoopModalOpen(true);
                  }} disabled={isReadOnly} data-testid="configure-loop-btn">Configure</Button>
                </div>
              </Tooltip>
            </InlineStack>


            {/* AfterShip Returns */}
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="0">
                <Text as="span" fontWeight="bold">AfterShip Returns</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="bodySm" tone="subdued">Receive return events</Text>
                  {isAftershipConnected
                    ? (aftershipWebhookStale
                      ? <Badge tone="warning" size="small">Token OK • No Webhooks</Badge>
                      : <Badge tone="success" size="small">Active</Badge>)
                    : <Badge size="small">Not Connected</Badge>}
                </InlineStack>
              </BlockStack>
              <Tooltip content="Manage your AfterShip Returns connection and webhook settings.">
                <div style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                  <Button variant="secondary" onClick={() => {
                    if (isReadOnly) {
                      const shopify = (window as any).shopify;
                      shopify?.toast.show("Integration settings are locked in demo mode.", { duration: 3000, isError: true });
                      return;
                    }
                    setAftershipModalOpen(true);
                  }} disabled={isReadOnly}>Configure</Button>
                </div>
              </Tooltip>
            </InlineStack>

            {/* Gorgias */}
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="0">
                <Text as="span" fontWeight="bold" tone="subdued">Gorgias</Text>
                <Text as="p" variant="bodySm" tone="subdued">Sync recovery notes to tickets</Text>
              </BlockStack>
              <Box paddingInlineStart="300" paddingInlineEnd="300" paddingBlockStart="100" paddingBlockEnd="100" background="bg-surface-secondary" borderRadius="200">
                <Text variant="bodySm" tone="subdued" as="p">Coming Soon</Text>
              </Box>
            </InlineStack>

            {/* Klaviyo */}
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="0">
                <Text as="span" fontWeight="bold" tone="subdued">Klaviyo</Text>
                <Text as="p" variant="bodySm" tone="subdued">Trigger retention flows</Text>
              </BlockStack>
              <Box paddingInlineStart="300" paddingInlineEnd="300" paddingBlockStart="100" paddingBlockEnd="100" background="bg-surface-secondary" borderRadius="200">
                <Text variant="bodySm" tone="subdued" as="p">Coming Soon</Text>
              </Box>
            </InlineStack>
          </BlockStack>

          {isGated && (
            <Box paddingBlockStart="200">
              <Text variant="bodySm" tone="subdued" as="p">
                Note: Performance integrations are available on Pro and Enterprise plans.
              </Text>
            </Box>
          )}

          {/* Sync Historical Data Button */}
          <Box paddingBlockStart="400" paddingBlockEnd="200">
            <BlockStack gap="200">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="0">
                  <Text as="span" fontWeight="bold">Historical Data Sync</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Pull past return data to populate your dashboard</Text>
                </BlockStack>
                <Tooltip content={isRechargeConnected && isLoopConnected ? "Sync historical returns data from Loop and Recharge." : "You must connect both Recharge and Loop Returns before syncing historical data."}>
                  <div style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                    <Button
                      variant="primary"
                      onClick={() => {
                        if (isReadOnly) {
                          const shopify = (window as any).shopify;
                          shopify?.toast.show("Data sync is locked in demo mode.", { duration: 3000, isError: true });
                          return;
                        }
                        fetcher.submit(
                          { actionType: "RESTORE_DEMO_DATA", snapshot: "BASELINE" },
                          { method: "post" }
                        );
                      }}
                      disabled={!isRechargeConnected || (!isLoopConnected && !isAftershipConnected)}
                      loading={fetcher.state !== "idle"}
                      data-testid="sync-historical-data-btn"
                    >
                      Sync Historical Returns Data
                    </Button>
                  </div>
                </Tooltip>
              </InlineStack>
            </BlockStack>
          </Box>
        </BlockStack>
      </Card>

      {/* Recharge Modal */}
      <Modal
        open={rechargeModalOpen}
        onClose={handleCloseRecharge}
        title="Configure Recharge"
        primaryAction={{
          content: "Save Connection",
          onAction: handleSaveRecharge,
          loading: isSubmitting,
        }}
        secondaryActions={[{ content: "Cancel", onAction: handleCloseRecharge }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {rechargeTestStatus === "success" && (
              <Banner tone="success" title="Connection successful">
                <p>Your Recharge API token is valid and ready to use.</p>
              </Banner>
            )}
            {rechargeTestStatus === "fail" && (
              <Banner tone="critical" title="Invalid token">
                <p>
                  This token doesn't look right. Make sure you've copied your full Recharge API token. 
                  For testing, you can use <strong>sk_live_8f7b2c9a1d3e4f6b5a8c7d9e2f1b0c3a</strong>.
                </p>
                <div style={{ marginTop: "8px" }}>
                  <Button url="https://apps.shopify.com/recharge-subscriptions" external variant="tertiary">
                    Install Recharge
                  </Button>
                </div>
              </Banner>
            )}
            {error && rechargeModalOpen && rechargeTestStatus === "idle" && (
              <Banner tone="critical" title="Could not save">
                <p>{error}</p>
              </Banner>
            )}
            <FormLayout>
              <TextField
                label="Recharge API Token"
                value={rechargeToken}
                onChange={(val) => { setRechargeToken(val); setRechargeTestStatus("idle"); }}
                autoComplete="off"
                helpText="Generate an API token in your Recharge Admin under Integrations > API Tokens."
                type="password"
                disabled={isSubmitting}
                data-testid="recharge-token-input"
              />
              <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                <Button onClick={handleTestRecharge} disabled={!rechargeToken || isSubmitting} data-testid="test-recharge-btn">
                  Test Connection
                </Button>
              </div>
            </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Loop Modal */}
      <Modal
        open={loopModalOpen}
        onClose={handleCloseLoop}
        title="Configure Loop Returns"
        primaryAction={{
          content: "Save Connection",
          onAction: handleSaveLoop,
          loading: isSubmitting,
        }}
        secondaryActions={[{ content: "Cancel", onAction: handleCloseLoop }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {loopTestStatus === "success" && (
              <Banner tone="success" title="Connection successful">
                <p>Your Loop webhook secret is valid and ready to use.</p>
              </Banner>
            )}
            {loopTestStatus === "fail" && (
              <Banner tone="critical" title="Invalid secret">
                <p>
                  This secret doesn't look right. Make sure you've copied your Loop webhook secret exactly as shown in your Loop dashboard.
                  For testing, you can use <strong>whsec_4b9c1d2e8f7a6b5c4d3e2f1a9b8c7d6e</strong>.
                </p>
                <div style={{ marginTop: "8px" }}>
                  <Button url="https://apps.shopify.com/loop-returns" external variant="tertiary">
                    Install Loop Returns
                  </Button>
                </div>
              </Banner>
            )}
            {error && loopModalOpen && loopTestStatus === "idle" && (
              <Banner tone="critical" title="Could not save">
                <p>{error}</p>
              </Banner>
            )}
            <FormLayout>
              <TextField
                label="Loop Webhook Secret"
                value={loopSecret}
                onChange={(val) => { setLoopSecret(val); setLoopTestStatus("idle"); }}
                autoComplete="off"
                helpText="Paste the Webhook Secret provided by Loop for your webhook endpoint."
                type="password"
                disabled={isSubmitting}
                data-testid="loop-secret-input"
              />
              <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                <Button onClick={handleTestLoop} disabled={!loopSecret || isSubmitting} data-testid="test-loop-btn">
                  Test Connection
                </Button>
              </div>
            </FormLayout>

            {loopTestStatus === "success" && (
              <BlockStack gap="400">
                <Banner tone="info" title="Next: Configure your Loop webhook URL">
                  <BlockStack gap="200">
                    <Text as="p">
                      Paste this URL into your Loop Returns admin under <strong>Settings → Webhooks → Add Endpoint</strong>:
                    </Text>
                    <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" fontWeight="bold" variant="bodyMd">
                          {`https://revivify-ai.com/webhooks/loop`}
                        </Text>
                        <Button
                          size="slim"
                          onClick={() => {
                            navigator.clipboard.writeText("https://revivify-ai.com/webhooks/loop");
                            const shopify = (window as any).shopify;
                            shopify?.toast?.show("URL copied to clipboard", { duration: 2000 });
                          }}
                        >
                          Copy
                        </Button>
                      </InlineStack>
                    </Box>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Once configured, Loop will send return.created events to this URL. Each request is authenticated
                      using the webhook secret you provided above, ensuring only your store's returns are processed.
                    </Text>
                  </BlockStack>
                </Banner>
              </BlockStack>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* AfterShip Modal */}
      <Modal
        open={aftershipModalOpen}
        onClose={handleCloseAftership}
        title="Configure AfterShip Returns"
        primaryAction={{
          content: "Save Connection",
          onAction: handleSaveAftership,
          loading: isSubmitting,
        }}
        secondaryActions={[{ content: "Cancel", onAction: handleCloseAftership }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {aftershipTestStatus === "success" && (
              <Banner tone="success" title="Connection successful">
                <p>Your AfterShip webhook secret is valid and ready to use.</p>
              </Banner>
            )}
            {aftershipTestStatus === "fail" && (
              <Banner tone="critical" title="Invalid secret">
                <p>
                  This secret doesn't look right. Find your webhook secret in your AfterShip admin
                  under Settings → Notifications → Webhooks.
                </p>
              </Banner>
            )}
            <FormLayout>
              <TextField
                label="AfterShip Webhook Secret"
                value={aftershipSecret}
                onChange={(val) => { setAftershipSecret(val); setAftershipTestStatus("idle"); }}
                autoComplete="off"
                helpText="Paste the Webhook Secret from AfterShip Returns admin."
                type="password"
                disabled={isSubmitting}
              />
              <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                <Button onClick={() => {
                  if (isTokenValid(aftershipSecret)) setAftershipTestStatus("success");
                  else setAftershipTestStatus("fail");
                }} disabled={!aftershipSecret || isSubmitting}>
                  Test Connection
                </Button>
              </div>
            </FormLayout>

            {aftershipTestStatus === "success" && (
              <BlockStack gap="400">
                <Banner tone="info" title="Next: Configure your AfterShip webhook URL">
                  <BlockStack gap="200">
                    <Text as="p">
                      Paste this URL into your AfterShip admin under <strong>Settings → Notifications → Webhooks</strong>:
                    </Text>
                    <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" fontWeight="bold" variant="bodyMd">
                          {`https://revivify-ai.com/webhooks/aftership`}
                        </Text>
                        <Button
                          size="slim"
                          onClick={() => {
                            navigator.clipboard.writeText("https://revivify-ai.com/webhooks/aftership");
                            const shopify = (window as any).shopify;
                            shopify?.toast?.show("URL copied to clipboard", { duration: 2000 });
                          }}
                        >
                          Copy
                        </Button>
                      </InlineStack>
                    </Box>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Once configured, AfterShip will send return.created events to this URL. Each request is authenticated
                      using the webhook secret you provided above.
                    </Text>
                  </BlockStack>
                </Banner>
              </BlockStack>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Box>
  );
}
